import {
  AssetExtension,
  copySearchParams,
  ExtensionType,
  Loader,
  LoaderParserPriority,
  path,
  ResolvedAsset,
  Resolver,
  Spritesheet,
  SpritesheetData,
  Texture,
  UnresolvedAsset
} from 'pixi.js';

/* eslint-disable */
// Disabling eslint due to this being a pixi extension which doesn't necessarily follow our standards.
export interface animationInfo {
  prefix: string;
  start: number;
  end: number;
  repeat: number;
}

export interface animationClips { [key: string]: animationInfo }

export interface SpriteSheetMetaData extends SpritesheetData {
  meta: {
    image: string;
    scale: string;
    related_multi_packs?: string[];
    clips: animationClips;
    frames: { [key: string]: string };
    frameRate: number;
  };
}

const validImages = ['jpg', 'png', 'jpeg', 'avif', 'webp',
  's3tc', 's3tc_sRGB', 'etc', 'etc1', 'pvrtc', 'atc', 'astc', 'bptc'];

function getCacheableAssets(keys: string[], asset: Spritesheet, ignoreMultiPack: boolean) {
  const out: Record<string, Texture | Spritesheet> = {};

  keys.forEach((key: string) => {
    out[key] = asset;
  });

  Object.keys(asset.textures).forEach((key) => {
    out[`${asset.cachePrefix}${key}`] = asset.textures[key];
  });

  if (!ignoreMultiPack) {
    const basePath = path.dirname(keys[0]);

    asset.linkedSheets.forEach((item: Spritesheet, i) => {
      Object.assign(out, getCacheableAssets(
        [`${basePath}/${asset.data.meta.related_multi_packs![i]}`],
        item,
        true
      ));
    });
  }

  return out;
}

/**
 * Asset extension for loading spritesheets.
 * @memberof PIXI
 * @type {PIXI.AssetExtension}
 */
export const extendedSpritesheetAsset = {
  extension: ExtensionType.Asset,
  /** Handle the caching of the related Spritesheet Textures */
  cache: {
    test: (asset: Spritesheet) => asset instanceof Spritesheet,
    getCacheableAssets: (keys: string[], asset: Spritesheet) => getCacheableAssets(keys, asset, false)
  },
  /** Resolve the the resolution of the asset. */
  resolver: {
    test: (value: string): boolean => {
      const tempURL = value.split('?')[0];
      const split = tempURL.split('.');
      const extension = split.pop();
      const format = split.pop();

      return extension === 'json' && validImages.includes(format!);
    },
    parse: (value: string): UnresolvedAsset => {
      const split = value.split('.');

      return {
        resolution: parseFloat(Resolver.RETINA_PREFIX?.exec(value)?.[1] ?? '1'),
        format: split[split.length - 2],
        src: value
      };
    }
  },
  /**
     * Loader plugin that parses sprite sheets!
     * once the JSON has been loaded this checks to see if the JSON is spritesheet data.
     * If it is, we load the spritesheets image and parse the data into PIXI.Spritesheet
     * All textures in the sprite sheet are then added to the cache
     * @ignore
     */
  loader: {
    name: 'extendedSpritesheetLoader',
    id: 'spriteSheet',

    extension: {
      type: ExtensionType.LoadParser,
      priority: LoaderParserPriority.High,
      name: 'extendedSpritesheetLoader'
    },

    async testParse(asset: SpriteSheetMetaData, options: ResolvedAsset): Promise<boolean> {
      return (path.extname(options.src!).toLowerCase() === '.json' && !!asset.frames);
    },

    async parse(asset: SpriteSheetMetaData, options: ResolvedAsset, loader: Loader): Promise<Spritesheet> {
      const {
        texture: imageTexture, // if user need to use preloaded texture
        imageFilename, // if user need to use custom filename (not from jsonFile.meta.image)
        textureOptions, // if user need to set texture options on texture
        cachePrefix // if user need to use custom cache prefix
      } = options?.data ?? {};

      let basePath = path.dirname(options.src!);

      if (basePath && basePath.lastIndexOf('/') !== (basePath.length - 1)) {
        basePath += '/';
      }

      let texture: Texture;

      if (imageTexture instanceof Texture) {
        texture = imageTexture;
      } else {
        const imagePath = copySearchParams(basePath + (imageFilename ?? asset.meta.image), options.src!);

        const assets = await loader.load<Texture>([{ src: imagePath, data: textureOptions }]);

        texture = assets[imagePath];
      }

      const spritesheet = new Spritesheet({
        texture: texture.source,
        data: asset,
        resolutionFilename: options.src,
        cachePrefix
      });

      await spritesheet.parse();
      Object.assign(asset.meta, options.data);

      // Check and add the multi atlas
      // Heavily influenced and based on https://github.com/rocket-ua/pixi-tps-loader/blob/master/src/ResourceLoader.js
      // eslint-disable-next-line camelcase
      const multiPacks = asset?.meta?.related_multi_packs;

      if (Array.isArray(multiPacks)) {
        const promises: Promise<Spritesheet<SpriteSheetMetaData>>[] = [];

        for (const item of multiPacks) {
          if (typeof item !== 'string') {
            continue;
          }

          let itemUrl = basePath + item;

          // Check if the file wasn't already added as multipack
          if (options.data?.ignoreMultiPack) {
            continue;
          }

          itemUrl = copySearchParams(itemUrl, options.src!);

          promises.push(loader.load<Spritesheet<SpriteSheetMetaData>>({
            src: itemUrl,
            data: {
              textureOptions,
              ignoreMultiPack: true
            }
          }));
        }

        const res = await Promise.all(promises);

        spritesheet.linkedSheets = res;
        res.forEach((item) => {
          item.linkedSheets = [spritesheet].concat(spritesheet.linkedSheets.filter((sp) => (sp !== item)));
        });
      }

      return spritesheet;
    },

    async unload(spritesheet: Spritesheet, _resolvedAsset, loader: Loader) {
      await loader.unload(spritesheet.textureSource._sourceOrigin);

      spritesheet.destroy(true);
    }
  }
} as AssetExtension<Spritesheet | SpriteSheetMetaData>;
